/**
 * FinancingFlow.js — Financing contingency: milestone tracker, extension request.
 */

import React, { useState } from 'react';
import { useFinancingMilestones } from '../../agents/contingencyAgent';
import { toDateOnly } from '../OfferManagement/utils';

function CompactRail({ steps, currentIndex }) {
  return (
    <div className="ch-compact-rail" role="list" aria-label="Financing progress timeline">
      {steps.map((step, idx) => {
        const done = idx < currentIndex;
        const current = idx === currentIndex;
        return (
          <div className="ch-compact-rail-item" role="listitem" key={step.label}>
            <span className={`ch-compact-rail-dot${done ? ' is-done' : ''}${current ? ' is-current' : ''}`} aria-hidden="true">
              {done ? '✓' : idx + 1}
            </span>
            <span className={`ch-compact-rail-label${current ? ' is-current' : ''}`}>
              {step.icon ? <span className="ch-compact-rail-label-icon" aria-hidden="true">{step.icon}</span> : null}
              {step.label}
            </span>
            {idx < steps.length - 1 && <span className="ch-compact-rail-line" aria-hidden="true" />}
          </div>
        );
      })}
    </div>
  );
}

const MILESTONE_META = {
  pre_approval_letter:    { icon: '', label: 'Pre-Approval Letter' },
  loan_application:       { icon: '', label: 'Loan Application' },
  appraisal_ordered:      { icon: '', label: 'Appraisal Ordered' },
  underwriting:           { icon: '', label: 'Underwriting Review' },
  conditional_approval:   { icon: '', label: 'Conditional Approval' },
  clear_to_close:         { icon: '', label: 'Clear to Close' },
};

const STATUS_LABELS = {
  pending:     { label: 'Pending',     color: '#a0aec0' },
  in_progress: { label: 'In Progress', color: '#d97706' },
  complete:    { label: 'Complete',    color: '#38a169' },
  waived:      { label: 'Waived',      color: '#4f46e5' },
  blocked:     { label: 'Blocked',     color: '#c53030' },
};

export default function FinancingFlow({ contingency, onRefresh }) {
  const { milestoneData, loading, error, saving, updateMilestone, requestExtension } = useFinancingMilestones(contingency?.id);
  const [extDate, setExtDate] = useState('');
  const [extReason, setExtReason] = useState('');
  const [msg, setMsg] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [milestoneNotes, setMilestoneNotes] = useState({});

  const milestones = milestoneData?.milestones || [];
  const progress = milestoneData?.progress_percent || 0;
  const financingSteps = [
    { label: 'Received', icon: '📥' },
    { label: 'In Progress', icon: '🏦' },
    { label: 'Final Review', icon: '🔎' },
    { label: 'Resolved', icon: '✅' },
  ];
  const financingRailIndex =
    contingency?.status === 'satisfied' ? 3
      : progress >= 85 ? 2
        : progress >= 30 ? 1
          : 0;

  const handleMilestoneUpdate = async (milestoneId, status) => {
    const notes = milestoneNotes[milestoneId] || '';
    const result = await updateMilestone(milestoneId, status, notes);
    if (result.success) {
      setMsg({ type: 'success', text: 'Milestone updated.' });
      setExpandedId(null);
      onRefresh?.();
    } else {
      setMsg({ type: 'error', text: result.error });
    }
  };

  const handleExtension = async () => {
    if (!extDate) {
      setMsg({ type: 'error', text: 'Select a new deadline date.' });
      return;
    }
    const result = await requestExtension(extDate, extReason);
    if (result.success) {
      setMsg({ type: 'success', text: 'Extension request submitted to seller.' });
      setExtDate('');
      setExtReason('');
      onRefresh?.();
    } else {
      setMsg({ type: 'error', text: result.error });
    }
  };

  if (loading) return <div className="ch-loading">Loading milestones…</div>;
  if (error) return <div className="ch-error">{error}</div>;

  return (
    <div>
      <div className="ch-card" style={{ padding: '0.85rem 1.05rem' }}>
        <CompactRail steps={financingSteps} currentIndex={financingRailIndex} />
      </div>

      {msg && (
        <div className={`ch-deadline-banner ${msg.type === 'error' ? 'critical' : 'low'}`} style={{ marginBottom: '0.75rem' }}>
          {msg.text}
        </div>
      )}

      {/* Progress bar */}
      <div className="ch-card">
        <div className="ch-card-title" style={{ marginBottom: '0.5rem' }}>
          Financing Milestones
          <span style={{ marginLeft: 'auto', fontSize: '0.9rem', fontWeight: 700, color: progress === 100 ? 'var(--green)' : 'var(--accent)' }}>
            {progress}%
          </span>
        </div>
        <div className="ch-progress-wrap">
          <div className="ch-progress-bar" style={{ width: `${progress}%` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
          <span>Start</span>
          <span>{milestoneData?.days_remaining != null ? `${milestoneData.days_remaining} days left` : ''}</span>
        </div>
      </div>

      {/* Milestone list */}
      <div className="ch-card">
        <div className="ch-milestone-list">
          {milestones.map((m, idx) => {
            const meta = MILESTONE_META[m.milestone_type] || { icon: '', label: m.milestone_type };
            const statusMeta = STATUS_LABELS[m.status] || { label: m.status, color: '#a0aec0' };
            const isExpanded = expandedId === m.id;
            const isComplete = m.status === 'complete';

            return (
              <div key={m.id} className={`ch-milestone-row${isComplete ? ' complete' : ''}`}>
                <span className="ch-milestone-icon">{meta.icon}</span>
                <div style={{ flex: 1 }}>
                  <div className="ch-milestone-name">{meta.label}</div>
                  {m.notes && <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{m.notes}</div>}
                  {m.completed_at && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      Completed: {new Date(m.completed_at).toLocaleDateString()}
                    </div>
                  )}
                  {isExpanded && !isComplete && (
                    <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }} onClick={e => e.stopPropagation()}>
                      <input
                        className="ch-input"
                        type="text"
                        placeholder="Notes (optional)"
                        value={milestoneNotes[m.id] || ''}
                        onChange={e => setMilestoneNotes(prev => ({ ...prev, [m.id]: e.target.value }))}
                      />
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {['in_progress', 'complete', 'waived'].map(st => (
                          <button key={st} className={`ch-btn ch-btn-${st === 'complete' ? 'success' : 'ghost'}`} style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                            onClick={() => handleMilestoneUpdate(m.id, st)} disabled={saving}>
                            {st.replace('_', ' ')}
                          </button>
                        ))}
                        <button className="ch-btn ch-btn-ghost" style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }} onClick={() => setExpandedId(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.3rem' }}>
                  <span className="ch-milestone-status" style={{ color: statusMeta.color }}>{statusMeta.label}</span>
                  {!isComplete && contingency?.status !== 'satisfied' && (
                    <button
                      className="ch-btn ch-btn-ghost"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      onClick={() => setExpandedId(isExpanded ? null : m.id)}
                    >
                      {isExpanded ? 'Close' : 'Update'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Extension request */}
      {!['satisfied', 'removed', 'exercised', 'expired'].includes(contingency?.status) && (
        <div className="ch-card">
          <div className="ch-card-title">Request Deadline Extension</div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="ch-form-row" style={{ flex: 1, minWidth: 160 }}>
              <label className="ch-form-label">New Deadline</label>
              <input className="ch-input" type="date" value={extDate} onChange={e => setExtDate(e.target.value)} min={toDateOnly(new Date())} />
            </div>
            <div className="ch-form-row" style={{ flex: 2, minWidth: 200 }}>
              <label className="ch-form-label">Reason</label>
              <input className="ch-input" type="text" value={extReason} onChange={e => setExtReason(e.target.value)} placeholder="e.g. Appraisal delayed" />
            </div>
            <button className="ch-btn ch-btn-primary" style={{ marginBottom: '0.75rem' }} onClick={handleExtension} disabled={saving || !extDate}>
              {saving ? 'Requesting…' : 'Request Extension'}
            </button>
          </div>
        </div>
      )}

      {/* Satisfied banner */}
      {contingency?.status === 'satisfied' && (
        <div className="ch-deadline-banner low">Clear to Close received — financing contingency satisfied!</div>
      )}
    </div>
  );
}
